import { Component } from '@theme/component';
import { ThemeEvents, VariantPreviewEvent, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';
import { requestIdleCallback } from '@theme/utilities';
import {
  buildPreviewImageUrl,
  buildPreviewSrcset,
  nextPreviewGeneration,
  parsePreviewMediaMetadata,
  selectPreviewMediaToWarm,
  shouldBridgePreviewThroughCommit,
  shouldPaintPreviewCandidate,
  shouldWarmPreviewMedia,
} from '@theme/preview-media';

/**
 * @typedef {object} PreviewImageLoad
 * @property {HTMLImageElement} image
 * @property {Promise<HTMLImageElement | null>} promise
 * @property {() => void} abort
 */

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 * @property {HTMLElement} [previewLayer] - Presentational featured-media preview overlay.
 * @property {HTMLImageElement} [previewImage] - Image inside the preview overlay.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    target?.addEventListener(ThemeEvents.variantPreview, this.#handleVariantPreview, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    this.#metadata = this.#readPreviewMediaMetadata();
    this.#mountPreviewLayer();
    this.#mediaQuery = window.matchMedia('(min-width: 750px)');
    this.#mediaQuery.addEventListener('change', this.#onBreakpointChange, { signal });
    window.addEventListener('pageshow', this.#handlePageShow, { signal });
    this.#schedulePreviewWarm();
  }

  #controller = new AbortController();

  /** @type {MediaQueryList | null} */
  #mediaQuery = null;

  /** @type {import('./preview-media').PreviewMediaMetadata | null} */
  #metadata = null;

  /** @type {number} */
  #previewGeneration = 0;

  /** @type {string | null} */
  #paintedMediaId = null;

  /** @type {number} */
  #productSelectInFlight = 0;

  /** @type {Map<string, PreviewImageLoad>} */
  #inflightImageLoads = new Map();

  /** @type {PreviewImageLoad | null} */
  #activePreviewIntentLoad = null;

  /** @type {Promise<void>} */
  #previewIntentCooldown = Promise.resolve();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#previewGeneration = nextPreviewGeneration(this.#previewGeneration);
    this.#productSelectInFlight = 0;
    this.#abortPreviewIntentLoad();
    for (const load of this.#inflightImageLoads.values()) load.abort();
    this.#inflightImageLoads.clear();
    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    this.#previewGeneration = nextPreviewGeneration(this.#previewGeneration);
    this.#abortPreviewIntentLoad();
    // Keep any painted hover preview as a bridge until the selected gallery replaces this
    // element — concealing early flashes the previously selected media under the click.
    this.#productSelectInFlight += 1;

    event.promise
      .then(({ detail }) => {
        this.#productSelectInFlight = Math.max(0, this.#productSelectInFlight - 1);
        if (!detail?.html) {
          this.#concealPreviewLayer();
          return;
        }

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) {
          this.#concealPreviewLayer();
          return;
        }

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        this.#productSelectInFlight = Math.max(0, this.#productSelectInFlight - 1);
        if (error?.name === 'AbortError') return;
        this.#concealPreviewLayer();
        console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles temporary color-preview intent from the main variant picker.
   *
   * @param {Event} event
   */
  #handleVariantPreview = (event) => {
    if (!(event instanceof VariantPreviewEvent)) return;

    const { clear, mediaId, generation } = event.detail;
    this.#previewGeneration = generation;

    if (clear || mediaId == null) {
      this.#abortPreviewIntentLoad();
      if (
        shouldBridgePreviewThroughCommit({
          productSelectInFlight: this.#productSelectInFlight,
          paintedMediaId: this.#paintedMediaId,
        })
      ) {
        return;
      }
      this.#concealPreviewLayer();
      return;
    }

    this.#showPreviewMedia(mediaId, generation);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  /** @returns {import('./preview-media').PreviewMediaMetadata | null} */
  #readPreviewMediaMetadata() {
    const script =
      this.previousElementSibling?.matches?.('script[data-variant-preview-media]')
        ? this.previousElementSibling
        : this.closest('.shopify-section')?.querySelector('script[data-variant-preview-media]');
    return parsePreviewMediaMetadata(script?.textContent?.trim());
  }

  #mountPreviewLayer() {
    const { previewLayer } = this.refs;
    if (!(previewLayer instanceof HTMLElement)) return;

    const host = this.#primaryPreviewHost();
    if (!(host instanceof HTMLElement)) return;

    if (getComputedStyle(host).position === 'static') {
      host.style.position = 'relative';
    }
    host.append(previewLayer);
  }

  #onBreakpointChange = () => {
    this.#mountPreviewLayer();
  };

  /** @param {PageTransitionEvent} event */
  #handlePageShow = (event) => {
    if (!event.persisted) return;
    this.#previewGeneration = nextPreviewGeneration(this.#previewGeneration);
    this.#abortPreviewIntentLoad();
    this.#concealPreviewLayer();
  };

  /** @returns {HTMLElement | null} */
  #primaryPreviewHost() {
    const desktopGrid =
      this.presentation === 'grid' && window.matchMedia('(min-width: 750px)').matches;
    if (desktopGrid) {
      const gridFirst = this.querySelector('.media-gallery__grid > .product-media-container');
      const gridMedia = gridFirst?.querySelector('.product-media[data-media-id]');
      if (gridMedia instanceof HTMLElement) return gridMedia;
    }

    const slide = this.querySelector('slideshow-slides > slideshow-slide');
    const slideMedia = slide?.querySelector('.product-media[data-media-id]');
    if (slideMedia instanceof HTMLElement) return slideMedia;

    const media = this.refs.media?.[0];
    const fallbackMedia = media?.querySelector('.product-media[data-media-id]');
    return fallbackMedia instanceof HTMLElement ? fallbackMedia : null;
  }

  /**
   * @param {string} mediaId
   * @param {number} generation
   */
  async #showPreviewMedia(mediaId, generation) {
    const descriptor = this.#metadata?.mediaById?.[mediaId];
    if (!descriptor?.src) {
      if (generation === this.#previewGeneration) this.#concealPreviewLayer();
      return;
    }

    const decoded = await this.#decodePreviewImage(descriptor, generation);
    if (!decoded) {
      if (generation === this.#previewGeneration) this.#concealPreviewLayer();
      return;
    }
    if (
      !shouldPaintPreviewCandidate({
        candidateGeneration: generation,
        currentGeneration: this.#previewGeneration,
        mediaId,
        decoded: true,
      })
    ) {
      return;
    }

    const { previewLayer, previewImage } = this.refs;
    if (!(previewLayer instanceof HTMLElement) || !(previewImage instanceof HTMLImageElement)) {
      return;
    }

    previewImage.src = decoded.src;
    previewImage.srcset = decoded.srcset;
    previewImage.sizes = decoded.sizes;
    previewImage.width = descriptor.width || decoded.naturalWidth || 0;
    previewImage.height = descriptor.height || decoded.naturalHeight || 0;
    previewImage.alt = '';
    if (descriptor.focal) {
      previewImage.style.setProperty('--focal-point', descriptor.focal);
      previewImage.style.objectPosition = descriptor.focal;
    }

    previewLayer.hidden = false;
    previewLayer.dataset.mediaId = mediaId;
    requestAnimationFrame(() => {
      if (
        !shouldPaintPreviewCandidate({
          candidateGeneration: generation,
          currentGeneration: this.#previewGeneration,
          mediaId,
          decoded: true,
        })
      ) {
        return;
      }
      previewLayer.classList.add('is-visible');
      this.#paintedMediaId = mediaId;
    });
  }

  /**
   * @param {import('./preview-media').PreviewMediaDescriptor} descriptor
   * @param {number} generation
   * @returns {Promise<HTMLImageElement | null>}
   */
  async #decodePreviewImage(descriptor, generation) {
    if (
      this.#activePreviewIntentLoad &&
      this.#activePreviewIntentLoad !== this.#inflightImageLoads.get(descriptor.id)
    ) {
      this.#abortPreviewIntentLoad();
    }

    await this.#previewIntentCooldown;
    if (generation !== this.#previewGeneration) return null;

    const load = this.#getOrStartImageLoad(descriptor, 'auto');
    this.#activePreviewIntentLoad = load;

    const image = await load.promise;
    if (this.#activePreviewIntentLoad === load) this.#activePreviewIntentLoad = null;
    return image;
  }

  /**
   * @param {import('./preview-media').PreviewMediaDescriptor} descriptor
   * @param {'auto' | 'low'} fetchPriority
   * @returns {PreviewImageLoad}
   */
  #getOrStartImageLoad(descriptor, fetchPriority) {
    const existing = this.#inflightImageLoads.get(descriptor.id);
    if (existing) {
      if (fetchPriority === 'auto') existing.image.fetchPriority = 'auto';
      return existing;
    }

    const widths = this.#metadata?.widths?.length
      ? this.#metadata.widths
      : [240, 352, 832, 1200, 1600, 1920, 2560, 3840];
    const sizes = this.#metadata?.sizes || '100vw';
    // Prefer a mid-tier candidate as the src fallback; srcset + sizes pick the real resource.
    const fallbackWidth = widths.find((width) => width >= 1200) ?? widths[widths.length - 1] ?? 1200;

    const image = new Image();
    image.decoding = 'async';
    image.fetchPriority = fetchPriority;
    image.sizes = sizes;

    /** @type {(image: HTMLImageElement | null) => void} */
    let resolveLoad = () => {};
    let settled = false;
    /** @type {Promise<HTMLImageElement | null>} */
    const promise = new Promise((resolve) => {
      resolveLoad = resolve;
    });
    /** @param {HTMLImageElement | null} result */
    const settle = (result) => {
      if (settled) return;
      settled = true;
      resolveLoad(result);
    };
    const abort = () => {
      if (settled) return;
      image.removeAttribute('srcset');
      image.removeAttribute('src');
      settle(null);
    };
    const load = { image, promise, abort };

    image.addEventListener(
      'load',
      async () => {
        try {
          await image.decode();
          settle(image);
        } catch {
          settle(null);
        }
      },
      { once: true }
    );
    image.addEventListener('error', () => settle(null), { once: true });

    this.#inflightImageLoads.set(descriptor.id, load);
    void promise.finally(() => {
      if (this.#inflightImageLoads.get(descriptor.id) === load) {
        this.#inflightImageLoads.delete(descriptor.id);
      }
    });

    image.srcset = buildPreviewSrcset(descriptor.src, widths);
    image.src = buildPreviewImageUrl(descriptor.src, fallbackWidth);
    return load;
  }

  #abortPreviewIntentLoad() {
    if (!this.#activePreviewIntentLoad) return;
    this.#activePreviewIntentLoad.abort();
    this.#activePreviewIntentLoad = null;
    this.#previewIntentCooldown = new Promise((resolve) =>
      requestAnimationFrame(() => requestAnimationFrame(() => resolve()))
    );
  }

  #schedulePreviewWarm() {
    const afterDocumentComplete = async () => {
      const selectedPrimaryDecoded = await this.#waitForSelectedPrimaryDecode();
      if (this.#controller.signal.aborted) return;

      const warmGateOpen = () => {
        const connection = navigator.connection;
        return shouldWarmPreviewMedia({
          documentReadyState: document.readyState,
          selectedPrimaryDecoded,
          saveData: connection?.saveData,
          effectiveType: connection?.effectiveType,
        });
      };
      if (!warmGateOpen()) return;

      requestIdleCallback(() => {
        if (this.#controller.signal.aborted || !warmGateOpen()) return;

        const selectedMediaId = this.#selectedPrimaryMediaId();
        for (const descriptor of selectPreviewMediaToWarm(this.#metadata, selectedMediaId)) {
          this.#getOrStartImageLoad(descriptor, 'low');
        }
      });
    };

    if (document.readyState === 'complete') {
      void afterDocumentComplete();
    } else {
      window.addEventListener('load', afterDocumentComplete, {
        once: true,
        signal: this.#controller.signal,
      });
    }
  }

  /** @returns {string | null} */
  #selectedPrimaryMediaId() {
    return this.#primaryPreviewHost()?.dataset.mediaId ?? null;
  }

  /** @returns {Promise<boolean>} */
  async #waitForSelectedPrimaryDecode() {
    const primary = this.#primaryPreviewHost()?.querySelector('.product-media__image');
    if (!(primary instanceof HTMLImageElement)) return false;

    if (!primary.complete) {
      /** @type {boolean} */
      const loaded = await new Promise((resolve) => {
        /** @param {boolean} result */
        const finish = (result) => {
          primary.removeEventListener('load', onLoad);
          primary.removeEventListener('error', onError);
          this.#controller.signal.removeEventListener('abort', onAbort);
          resolve(result);
        };
        const onLoad = () => finish(true);
        const onError = () => finish(false);
        const onAbort = () => finish(false);
        primary.addEventListener('load', onLoad, { once: true });
        primary.addEventListener('error', onError, { once: true });
        this.#controller.signal.addEventListener('abort', onAbort, { once: true });
      });
      if (!loaded) return false;
    }

    if (!primary.naturalWidth) return false;
    try {
      await primary.decode();
      return true;
    } catch {
      return false;
    }
  }

  #concealPreviewLayer() {
    const { previewLayer } = this.refs;
    if (!(previewLayer instanceof HTMLElement)) {
      this.#paintedMediaId = null;
      return;
    }

    previewLayer.classList.remove('is-visible');
    previewLayer.hidden = true;
    delete previewLayer.dataset.mediaId;
    this.#paintedMediaId = null;
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
